import {
  isAutocompleteFieldSchema,
  isCollectionFieldSchema,
  isMultipleChoiceFieldSchema,
} from "@w3rone/json-schema-form"
import type { FieldSchema, FormSchema } from "@w3rone/json-schema-form"
import * as R from "remeda"

export const getInitialValue = (
  schema: FormSchema | FieldSchema,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  entity: any,
) => {
  if (schema.type === "boolean") {
    return entity ?? schema.default ?? false
  }

  if (schema.type === "string") {
    if (schema.options.widget === "choice") {
      return entity?.enum_value ?? entity ?? schema.default ?? ""
    }

    if (schema.options.widget === "entity") {
      if (entity?.id) {
        return String(entity.id)
      } else if (schema.default) {
        return schema.default
      } else if (typeof entity === "string") {
        return entity
      } else {
        return ""
      }
    }

    // FIXME? money field with 0€ value are not hydrated because of the simple "if (entity)" below which returns false in case of a zero value (see #1051)
    if (schema.options.widget === "money" && 0 == entity) {
      return entity
    }

    if (entity) {
      if (entity.enum_value) {
        return entity.enum_value
      }

      if (entity.id) {
        return String(entity.id)
      }

      return entity
    }

    if (schema.default) {
      return schema.default
    }

    return ""
  }

  if (schema.type === "integer" || schema.type === "number") {
    if (entity !== undefined && entity !== null) {
      return String(entity)
    } else if (schema.default) {
      return schema.default
    } else {
      return ""
    }
  }

  if (schema.type === "object") {
    if (
      [
        FieldWidget.BIRTHDAY,
        FieldWidget.DATE,
        FieldWidget.DATETIME,
        FieldWidget.TIME,
        FieldWidget.TIME_CHOICE,
      ].includes(schema.options.widget)
    ) {
      const date = entity ? new Date(entity) : null

      return {
        year: date ? date.getUTCFullYear().toString() : "",
        month: date ? (date.getUTCMonth() + 1).toString() : "",
        day: date ? date.getUTCDate().toString() : "",
        hour: date ? date.getUTCHours().toString() : "",
        minute: date ? date.getUTCMinutes().toString() : "",
        second: date ? date.getUTCSeconds().toString() : "",
      }
    }

    if ([FieldWidget.IMAGE, FieldWidget.FILE].includes(schema.options.widget)) {
      return entity?.thumbs?.cache ?? entity?.url ?? ""
    }

    if (schema.options.widget === FieldWidget.ADDRESS) {
      const baseValue = getObjectInitialValue(schema, entity)

      if (entity) {
        return {
          ...baseValue,
          raw: baseValue.formatted,
        }
      }

      return baseValue
    }

    if (schema.options.widget === FieldWidget.PHONE) {
      return {
        country: entity?.countryAlpha2 || "",
        number: entity?.nationalNumber || "",
      }
    }

    if (isAutocompleteFieldSchema(schema)) {
      const entityFieldName = Object.keys(schema.properties).find(
        (key) => key !== "ac_keyword",
      )

      const value = {
        ac_keyword: entity ? entity[schema.options.autocomplete.label] : "",
        [entityFieldName || "id"]: entity
          ? entity[schema.options.autocomplete.id]
          : "",
      }

      return value
    }

    return getObjectInitialValue(schema, entity)
  }

  if (schema.type === "array") {
    if (isCollectionFieldSchema(schema)) {
      if (entity) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return entity.map((entity: any, index: number) => {
          const itemSchema = Array.isArray(schema.items)
            ? R.clone(schema.items[index])
            : R.clone(schema.items)

          if (!itemSchema) {
            console.error({ schema, index })
            throw new Error(
              `Couldn't find collection item schema: index ${index}`,
            )
          }

          /*
           * When a collection contains autocomplete items, the fields have item
           * index duplicated as names. For example user[partners][0][0] or
           * user[partners][1][1]. We need to handle this specific case in hydration.
           *
           * That's why we replace the entity field name (the one that is not ac_keyword)
           * with the index of the item in the collection in the schema.
           */
          if (isAutocompleteFieldSchema(itemSchema)) {
            const entityFieldName = Object.keys(itemSchema.properties).find(
              (key) => key !== "ac_keyword",
            )

            if (entityFieldName) {
              const property = itemSchema.properties[entityFieldName]

              if (property) {
                itemSchema.properties[index] = property
              }

              if (String(index) !== entityFieldName) {
                delete itemSchema.properties[entityFieldName]
              }
            }
          }

          return getInitialValue(itemSchema, entity)
        })
      }

      return []
    } else if (isMultipleChoiceFieldSchema(schema)) {
      if (entity) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return entity.map((entity: any) => {
          return entity?.enum_value || entity?.id?.toString() || entity || ""
        })
      }

      return []
    }
  }
}

const FieldWidget = {
  CHOICE: "choice",
  ENTITY: "entity",
  IMAGE: "vich_image",
  FILE: "vich_file",
  BIRTHDAY: "birthday",
  DATE: "date",
  DATETIME: "datetime",
  ADDRESS: "address",
  PHONE: "phone_number",
  AUTOCOMPLETE: "autocomplete",
  TIME: "time",
  TIME_CHOICE: "time_choice",
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getObjectInitialValue = (schema: any, entity: any): any => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Object.entries(schema.properties).reduce((value: any, entry: any) => {
    const [name, propertySchema] = entry
    const entityChunk = entity?.[name]

    value[name] = getInitialValue(propertySchema, entityChunk)

    return value
  }, {})
}
